/**
 * OrderChangeSheet — the overflow container for an order-change resolution
 * (BLU-695, hand-over locked decision 4: inline for 1 to 2 changed SKUs, this
 * side panel at 3+).
 *
 * It is a SHELL and nothing else: the plan, the reason field and both actions
 * are `OrderChangeResolution` rendered with `confirmMode="footer"`, exactly the
 * same component the inline card renders. One behaviour, two homes.
 *
 * Sheet shape mirrors `OrderDetailModal` (`side="right"`, `sm:max-w-2xl`) so the
 * board has one panel geometry. The extra `flex flex-col` turns it into a
 * header / scrolling body / pinned footer column, which is what makes the
 * two-state footer land in a fixed place instead of scrolling off the bottom.
 *
 * There is deliberately NO AlertDialog anywhere in this file. The confirm is a
 * second state of the footer (locked decision 5) — a dialog over a Sheet is the
 * stacked-modal trap DESIGN.md §10 and PRODUCTION_DESIGN.md §6.1 both ban, and
 * BLU-563 already hit it once.
 */
import { useTranslation } from 'react-i18next';
import { FileEdit } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { OrderChangeResolution, type BinMoveResult } from './OrderChangeResolution';

export interface OrderChangeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prodOrderId: string;
  /** Every OPEN ORDER_CHANGED issue row for this order (see OrderChangeResolution). */
  openIssueIds: string[];
  /** The order's display name, e.g. "#1049479". */
  orderLabel: string;
  /** Masked customer display, when the issue row carried one. */
  customerDisplay?: string;
  /** BLU-695 §2b — forwarded to `OrderChangeResolution`; the list-level
   *  container renders the durable banner. */
  onBinMove?: (result: BinMoveResult) => void;
}

export function OrderChangeSheet({
  open,
  onOpenChange,
  prodOrderId,
  openIssueIds,
  orderLabel,
  customerDisplay,
  onBinMove,
}: OrderChangeSheetProps) {
  const { t } = useTranslation('production');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-2xl">
        <SheetHeader className="shrink-0 pr-8">
          <SheetTitle className="flex items-center gap-2 text-xl font-semibold">
            <FileEdit className="h-5 w-5 text-orange-600" aria-hidden="true" />
            <span className="truncate">{orderLabel}</span>
            {customerDisplay && (
              <span className="truncate text-sm font-normal text-muted-foreground">
                {customerDisplay}
              </span>
            )}
          </SheetTitle>
          <SheetDescription>{t('issues.orderChanges.sheetDescription')}</SheetDescription>
        </SheetHeader>

        {/* `key` remounts the resolver per order so a reopened panel never shows
            the previous order's typed reason or confirm state. */}
        <OrderChangeResolution
          key={prodOrderId}
          prodOrderId={prodOrderId}
          orderLabel={orderLabel}
          openIssueIds={openIssueIds}
          confirmMode="footer"
          onResolved={() => onOpenChange(false)}
          onBinMove={onBinMove}
        />
      </SheetContent>
    </Sheet>
  );
}
